import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiError } from "@/contexts/AuthContext";
import { PageHeader, PageBody, ErrorBlock } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Inbox as InboxIcon, Mail, Copy, Check, Archive, ArchiveRestore,
  RotateCcw, Send, Sparkles, Shield, KeyRound, ShoppingBag, Bell,
  MessageSquare, Tag, AlertTriangle, Filter, Loader2, RefreshCw,
} from "lucide-react";

// ------------------------------------------------------------------
// Category metadata: icon, color tone, label
// ------------------------------------------------------------------
const CATEGORY_META = {
  "2fa_code": { label: "2FA Code", icon: KeyRound, tone: "amber" },
  verification_code: { label: "Verify", icon: Shield, tone: "blue" },
  comment_notification: { label: "Comments", icon: MessageSquare, tone: "violet" },
  merchant_outreach: { label: "Merchant", icon: ShoppingBag, tone: "emerald" },
  account_alert: { label: "Alert", icon: AlertTriangle, tone: "rose" },
  order_confirmation: { label: "Order", icon: Tag, tone: "cyan" },
  marketing_spam: { label: "Spam", icon: Bell, tone: "zinc" },
  other: { label: "Other", icon: Mail, tone: "zinc" },
};

const TONE_CLASSES = {
  amber: "border-amber-700/60 text-amber-300 bg-amber-500/10",
  blue: "border-blue-700/60 text-blue-300 bg-blue-500/10",
  violet: "border-violet-700/60 text-violet-300 bg-violet-500/10",
  emerald: "border-emerald-700/60 text-emerald-300 bg-emerald-500/10",
  rose: "border-rose-700/60 text-rose-300 bg-rose-500/10",
  cyan: "border-cyan-700/60 text-cyan-300 bg-cyan-500/10",
  zinc: "border-zinc-700 text-zinc-400 bg-zinc-800/30",
};

const FILTER_PILLS = [
  { key: null, label: "All" },
  { key: "2fa_code,verification_code", label: "2FA Codes" },
  { key: "comment_notification", label: "Comments" },
  { key: "merchant_outreach", label: "Merchants" },
  { key: "account_alert", label: "Alerts" },
  { key: "order_confirmation", label: "Orders" },
  { key: "marketing_spam", label: "Spam" },
];

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    }
    const days = Math.floor((now - d) / 86400000);
    if (days < 7) return d.toLocaleDateString([], { weekday: "short" });
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch { return ""; }
}

// ------------------------------------------------------------------
// Category chip
// ------------------------------------------------------------------
function CategoryChip({ classification, confidence }) {
  if (!classification) {
    return (
      <Badge variant="outline" className="border-zinc-700 text-zinc-500 text-[10px]" data-testid="chip-classifying">
        <Loader2 className="h-2.5 w-2.5 mr-1 animate-spin" />classifying…
      </Badge>
    );
  }
  const meta = CATEGORY_META[classification] || CATEGORY_META.other;
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className={`text-[10px] ${TONE_CLASSES[meta.tone]}`}
           title={confidence ? `confidence ${(confidence * 100).toFixed(0)}%` : undefined}
           data-testid={`chip-${classification}`}>
      <Icon className="h-2.5 w-2.5 mr-1" />{meta.label}
    </Badge>
  );
}

// ------------------------------------------------------------------
// Giant 2FA copy button
// ------------------------------------------------------------------
function TwoFAButton({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(`Code ${code} copied`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <button onClick={copy}
            className={`w-full rounded-lg border-2 px-6 py-7 flex flex-col items-center justify-center gap-1 transition-all
                       ${copied
                         ? "border-emerald-500 bg-emerald-500/15"
                         : "border-amber-600 bg-amber-500/10 hover:bg-amber-500/15 hover:border-amber-500"}`}
            data-testid="twofa-copy-btn">
      <div className="text-[10px] mono uppercase text-zinc-400 tracking-widest">
        {copied ? "Copied to clipboard" : "Tap to copy"}
      </div>
      <div className={`text-5xl font-bold mono tracking-[0.3em] ${copied ? "text-emerald-300" : "text-amber-200"}`}
           data-testid="twofa-code">{code}</div>
      <div className={`flex items-center gap-1.5 text-xs ${copied ? "text-emerald-300" : "text-amber-400"}`}>
        {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy code</>}
      </div>
    </button>
  );
}

// ------------------------------------------------------------------
// Email row in the left column
// ------------------------------------------------------------------
function EmailRow({ email, selected, onClick }) {
  const unread = !email.is_read;
  return (
    <button onClick={onClick}
            className={`w-full text-left p-3 border-b border-zinc-800/60 transition-colors
                       ${selected ? "bg-emerald-500/10 border-l-2 border-l-emerald-500"
                                  : "hover:bg-zinc-900/50 border-l-2 border-l-transparent"}`}
            data-testid={`inbox-row-${email.id}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className={`text-xs truncate min-w-0 flex-1 ${unread ? "font-semibold text-zinc-100" : "text-zinc-400"}`}>
          {email.from_name || email.from_email}
        </div>
        <div className="text-[10px] mono text-zinc-500 shrink-0">{fmtTime(email.received_at)}</div>
      </div>
      <div className={`text-xs truncate mb-1.5 ${unread ? "text-zinc-200" : "text-zinc-500"}`}>
        {email.subject || <span className="italic text-zinc-600">(no subject)</span>}
      </div>
      <div className="flex items-center gap-1.5">
        <CategoryChip classification={email.classification}
                      confidence={email.classification_confidence} />
        {unread && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />}
        {email.extracted_2fa_code && (
          <Badge variant="outline" className="border-amber-700/60 bg-amber-500/10 text-amber-300 text-[10px] mono">
            {email.extracted_2fa_code}
          </Badge>
        )}
        <span className="text-[10px] mono text-zinc-600 truncate ml-auto">{email.mailbox_address}</span>
      </div>
    </button>
  );
}

// ------------------------------------------------------------------
// Right column: email detail
// ------------------------------------------------------------------
function EmailDetail({ email, onChange, onArchive, onMarkUnread }) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);

  useEffect(() => {
    setReply(email?.suggested_reply || "");
    setReplyOpen(!!email?.suggested_reply);
  }, [email?.id, email?.suggested_reply]);

  if (!email) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-zinc-600 p-8 text-sm">
        <Mail className="h-12 w-12 text-zinc-700 mb-3" />
        Select an email to view it here.
      </div>
    );
  }

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/inbox/${email.id}/reply`, { body: reply });
      toast.success(`Sent · ${r.data.sent_id?.slice(0, 8)}…`);
      setReply(""); setReplyOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "compliance_blocked") {
        const lint = detail.lint || {};
        const issues = [
          ...(lint.banned_terms_found || []),
          ...(lint.fake_scarcity_terms || []),
          ...(lint.medical_claims_found || []),
        ];
        toast.error(`Compliance blocked: ${issues.join(", ") || "missing disclosure"}`);
      } else {
        toast.error(formatApiError(detail) || "Send failed");
      }
    } finally { setSending(false); }
  };

  const meta = CATEGORY_META[email.classification] || CATEGORY_META.other;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-5 py-4 border-b border-zinc-800 shrink-0">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <div className="text-base font-semibold text-zinc-100 break-words" data-testid="detail-subject">
              {email.subject || <span className="italic text-zinc-600">(no subject)</span>}
            </div>
            <div className="text-xs text-zinc-500 mt-1 truncate">
              <span className="text-zinc-300">{email.from_name || email.from_email}</span>
              {email.from_name && <span className="mono text-zinc-600"> · {email.from_email}</span>}
              <span className="mono text-zinc-600"> → {email.mailbox_address}</span>
            </div>
            <div className="text-[10px] mono text-zinc-600 mt-0.5">{fmtTime(email.received_at)}</div>
          </div>
          <CategoryChip classification={email.classification} confidence={email.classification_confidence} />
        </div>
        <div className="flex items-center gap-1.5 mt-3">
          <Button size="sm" variant="outline"
                  className="border-zinc-700 bg-zinc-900 text-zinc-200 text-xs"
                  onClick={() => setReplyOpen((v) => !v)}
                  data-testid="detail-reply-btn">
            <Send className="h-3 w-3 mr-1.5" /> Reply
          </Button>
          <Button size="sm" variant="outline"
                  className="border-zinc-700 bg-zinc-900 text-zinc-200 text-xs"
                  onClick={onMarkUnread} data-testid="detail-unread-btn">
            <RotateCcw className="h-3 w-3 mr-1.5" /> Unread
          </Button>
          <Button size="sm" variant="outline"
                  className="border-zinc-700 bg-zinc-900 text-zinc-200 text-xs"
                  onClick={onArchive} data-testid="detail-archive-btn">
            {email.is_archived
              ? <><ArchiveRestore className="h-3 w-3 mr-1.5" /> Unarchive</>
              : <><Archive className="h-3 w-3 mr-1.5" /> Archive</>}
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {/* 2FA code — the killer feature */}
        {email.extracted_2fa_code && <TwoFAButton code={email.extracted_2fa_code} />}

        {/* AI summary */}
        {email.ai_summary && (
          <div className="rounded-md border border-emerald-900/50 bg-emerald-500/5 p-3 flex items-start gap-2"
               data-testid="ai-summary">
            <Sparkles className="h-3.5 w-3.5 text-emerald-400 mt-0.5 shrink-0" />
            <div className="text-xs text-emerald-200/90 leading-relaxed">{email.ai_summary}</div>
          </div>
        )}

        {/* Suggested reply box */}
        {replyOpen && (
          <div className="rounded-md border border-zinc-700 bg-zinc-900/50 p-3 space-y-2" data-testid="reply-box">
            <div className="text-[10px] mono uppercase text-zinc-500 flex items-center gap-1.5">
              {email.suggested_reply
                ? <><Sparkles className="h-3 w-3 text-amber-400" /> AI-drafted reply · edit before sending</>
                : "Compose reply"}
            </div>
            <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={6}
                      placeholder="Type your reply…"
                      className="bg-zinc-950 border-zinc-800 text-zinc-100 text-sm"
                      data-testid="reply-textarea" />
            <div className="flex items-center justify-between gap-2">
              <div className="text-[10px] mono text-zinc-600">
                Sending from <span className="text-emerald-400">{email.mailbox_address}</span>
              </div>
              <Button onClick={send} disabled={sending || !reply.trim()} size="sm"
                      className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
                      data-testid="reply-send-btn">
                {sending ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Send className="h-3 w-3 mr-1.5" />}
                Send via Resend
              </Button>
            </div>
          </div>
        )}

        {/* Email body */}
        <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-4">
          <div className="text-[10px] mono uppercase text-zinc-600 mb-2">message</div>
          {email.body_html ? (
            <div className="prose prose-invert prose-sm max-w-none text-zinc-200 [&_*]:!text-zinc-200 [&_a]:!text-emerald-400"
                 dangerouslySetInnerHTML={{ __html: email.body_html }} data-testid="email-body-html" />
          ) : (
            <pre className="whitespace-pre-wrap text-xs text-zinc-300 leading-relaxed mono"
                 data-testid="email-body-text">{email.body_text || "(empty body)"}</pre>
          )}
        </div>

        {/* Attachments */}
        {email.attachments?.length > 0 && (
          <div className="text-[11px] mono text-zinc-500">
            {email.attachments.length} attachment{email.attachments.length === 1 ? "" : "s"}:{" "}
            {email.attachments.map((a) => a.filename).join(", ")}
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Main page
// ------------------------------------------------------------------
export default function InboxPage() {
  const [addresses, setAddresses] = useState([]);
  const [domain, setDomain] = useState("");
  const [resendConfigured, setResendConfigured] = useState(true);
  const [selectedAddress, setSelectedAddress] = useState(null); // null = all
  const [filter, setFilter] = useState(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const pollRef = useRef(null);

  // load addresses (and unread counts)
  const loadAddresses = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/inbox/addresses`);
      setAddresses(r.data.addresses || []);
      setDomain(r.data.domain || "");
      setResendConfigured(!!r.data.resend_configured);
    } catch (e) {
      setErr(e?.response?.data || { error: "load_failed", detail: String(e) });
    }
  }, []);

  // load inbox list
  const loadList = useCallback(async () => {
    const params = new URLSearchParams();
    if (selectedAddress) params.set("address", selectedAddress);
    if (filter) params.set("classification", filter);
    if (unreadOnly) params.set("unread_only", "true");
    params.set("limit", "100");
    try {
      const r = await axios.get(`${API}/inbox?${params.toString()}`);
      setItems(r.data.items || []);
      setLoading(false);
    } catch (e) {
      setErr(e?.response?.data || { error: "load_failed", detail: String(e) });
      setLoading(false);
    }
  }, [selectedAddress, filter, unreadOnly]);

  // load single email detail
  const loadDetail = useCallback(async (id) => {
    if (!id) { setSelectedEmail(null); return; }
    try {
      const r = await axios.get(`${API}/inbox/${id}`);
      setSelectedEmail(r.data);
      // mark read in local list immediately
      setItems((arr) => arr.map((x) => x.id === id ? { ...x, is_read: true } : x));
      loadAddresses();
    } catch (e) {
      toast.error("Failed to load email");
    }
  }, [loadAddresses]);

  useEffect(() => { loadAddresses(); }, [loadAddresses]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  // poll every 30s
  useEffect(() => {
    pollRef.current = setInterval(() => {
      loadList(); loadAddresses();
    }, 30000);
    return () => clearInterval(pollRef.current);
  }, [loadList, loadAddresses]);

  const totalUnread = useMemo(
    () => addresses.reduce((s, a) => s + (a.unread_count || 0), 0),
    [addresses]
  );

  const archive = async () => {
    if (!selectedEmail) return;
    const want = !selectedEmail.is_archived;
    try {
      await axios.post(`${API}/inbox/${selectedEmail.id}/${want ? "archive" : "unarchive"}`);
      toast.success(want ? "Archived" : "Unarchived");
      setSelectedEmail({ ...selectedEmail, is_archived: want });
      if (want) {
        setItems((arr) => arr.filter((x) => x.id !== selectedEmail.id));
        setSelectedId(null);
      }
      loadAddresses();
    } catch { toast.error("Failed"); }
  };

  const markUnread = async () => {
    if (!selectedEmail) return;
    try {
      await axios.post(`${API}/inbox/${selectedEmail.id}/mark-unread`);
      setSelectedEmail({ ...selectedEmail, is_read: false });
      setItems((arr) => arr.map((x) => x.id === selectedEmail.id ? { ...x, is_read: false } : x));
      loadAddresses();
    } catch { toast.error("Failed"); }
  };

  if (err) return <PageBody><ErrorBlock data={err} testid="inbox-error" /></PageBody>;

  return (
    <>
      <PageHeader
        icon={InboxIcon}
        title="Inbox"
        subtitle={`${totalUnread} unread${domain ? ` · ${domain}` : ""}`}
        testid="inbox-header"
        actions={
          <Button size="sm" variant="outline" onClick={() => { loadList(); loadAddresses(); }}
                  className="border-zinc-700 bg-zinc-900 text-zinc-200 text-xs"
                  data-testid="inbox-refresh-btn">
            <RefreshCw className="h-3 w-3 mr-1.5" /> Refresh
          </Button>
        }
      />

      {!resendConfigured && (
        <div className="mx-4 sm:mx-6 mt-4 rounded-md border border-amber-900/60 bg-amber-950/30 px-3 py-2 text-xs text-amber-200"
             data-testid="resend-not-configured">
          <AlertTriangle className="h-3.5 w-3.5 inline mr-1.5" />
          Resend API key or webhook secret not set. Add <span className="mono">RESEND_API_KEY</span> + <span className="mono">RESEND_WEBHOOK_SECRET</span> to backend env to enable inbound delivery.
        </div>
      )}

      <div className="px-4 sm:px-6 py-4 grid grid-cols-1 md:grid-cols-[340px_1fr] gap-4" style={{ minHeight: "calc(100vh - 200px)" }}>
        {/* Left column */}
        <div className="ap-panel flex flex-col overflow-hidden" data-testid="inbox-list-panel">
          {/* Address selector */}
          <div className="p-3 border-b border-zinc-800 space-y-2">
            <select value={selectedAddress || ""}
                    onChange={(e) => setSelectedAddress(e.target.value || null)}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-200"
                    data-testid="inbox-address-select">
              <option value="">{`All addresses (${totalUnread} unread)`}</option>
              {addresses.map((a) => (
                <option key={a.id} value={a.address}>
                  {`${a.label} · ${a.address}${a.unread_count > 0 ? ` (${a.unread_count})` : ""}`}
                </option>
              ))}
            </select>
            <div className="flex flex-wrap gap-1">
              {FILTER_PILLS.map((p) => (
                <button key={p.label}
                        onClick={() => setFilter(p.key)}
                        data-testid={`filter-${p.key || "all"}`}
                        className={`px-2 py-0.5 rounded-full text-[10px] border transition-colors ${
                          filter === p.key
                            ? "border-emerald-700/60 bg-emerald-500/10 text-emerald-300"
                            : "border-zinc-800 bg-zinc-900 text-zinc-500 hover:text-zinc-300"
                        }`}>
                  {p.label}
                </button>
              ))}
              <button onClick={() => setUnreadOnly((v) => !v)}
                      className={`px-2 py-0.5 rounded-full text-[10px] border transition-colors ${
                        unreadOnly
                          ? "border-emerald-700/60 bg-emerald-500/10 text-emerald-300"
                          : "border-zinc-800 bg-zinc-900 text-zinc-500 hover:text-zinc-300"
                      }`}
                      data-testid="filter-unread-only">
                <Filter className="h-2.5 w-2.5 inline mr-1" />Unread only
              </button>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto" data-testid="inbox-list">
            {loading ? (
              <div className="p-6 text-xs text-zinc-500 text-center">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center" data-testid="inbox-empty">
                <Mail className="h-8 w-8 text-zinc-700 mx-auto mb-2" />
                <div className="text-xs text-zinc-500">No emails yet. Send one to <span className="mono text-emerald-400">{addresses[0]?.address || "your inbox"}</span> to test.</div>
              </div>
            ) : items.map((email) => (
              <EmailRow key={email.id} email={email}
                        selected={selectedId === email.id}
                        onClick={() => setSelectedId(email.id)} />
            ))}
          </div>
        </div>

        {/* Right column */}
        <div className="ap-panel overflow-hidden" data-testid="inbox-detail-panel">
          <EmailDetail email={selectedEmail}
                       onArchive={archive}
                       onMarkUnread={markUnread} />
        </div>
      </div>
    </>
  );
}
